/**
 * Sprint 233.A — SocialAccountsPanel
 *
 * Talent-facing editor for Instagram / TikTok / YouTube social handles +
 * follower / engagement / avg-views metrics.
 *
 * Today: manual entry only (clearly labeled "Self-reported").
 * Soon:  OAuth Connect buttons (Instagram Graph, TikTok for Developers,
 *        YouTube Data API). We render disabled Connect buttons with a
 *        "Coming soon" tooltip so the architecture and UX are already in
 *        place when the live integration ships.
 *
 * No scraping. Self-reported metrics are flagged in the API response
 * (`data_source: 'manual'`) so the marketplace can label them honestly.
 */
import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Instagram, Music2, Youtube, Loader2, Save, CheckCircle2,
  AlertCircle, Link as LinkIcon, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const PLATFORM_META = {
  instagram: {
    label: 'Instagram',
    Icon: Instagram,
    color: 'text-pink-500',
    badge: 'bg-gradient-to-tr from-fuchsia-500 via-rose-500 to-amber-400',
    placeholder: '@yourhandle',
    metric_label: 'Followers',
    avg_label: 'Avg Likes',
  },
  tiktok: {
    label: 'TikTok',
    Icon: Music2,
    color: 'text-slate-800',
    badge: 'bg-slate-900',
    placeholder: '@yourhandle',
    metric_label: 'Followers',
    avg_label: 'Avg Views',
  },
  youtube: {
    label: 'YouTube',
    Icon: Youtube,
    color: 'text-red-600',
    badge: 'bg-red-600',
    placeholder: 'Channel handle or URL',
    metric_label: 'Subscribers',
    avg_label: 'Avg Views',
  },
};

const PlatformRow = ({ platform, value, onSave, onRemove, oauthStatus }) => {
  const meta = PLATFORM_META[platform];
  const Icon = meta.Icon;
  const [form, setForm] = useState({
    handle: value?.handle || '',
    follower_count: value?.follower_count ?? '',
    engagement_rate: value?.engagement_rate ?? '',
    average_views: value?.average_views ?? '',
    profile_url: value?.profile_url || '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      handle: value?.handle || '',
      follower_count: value?.follower_count ?? '',
      engagement_rate: value?.engagement_rate ?? '',
      average_views: value?.average_views ?? '',
      profile_url: value?.profile_url || '',
    });
  }, [value?.handle]);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!form.handle.trim()) {
      toast.error('Handle is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        handle: form.handle.trim(),
        profile_url: form.profile_url.trim() || undefined,
        follower_count: form.follower_count !== '' ? Number(form.follower_count) : null,
        engagement_rate: form.engagement_rate !== '' ? Number(form.engagement_rate) : null,
        average_views: form.average_views !== '' ? Number(form.average_views) : null,
      };
      const r = await api.put(`/talent/social-accounts/${platform}`, payload);
      onSave?.(r.data);
      toast.success(`${meta.label} saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const verified = value?.is_verified_connection;
  const source = value?.data_source || 'manual';
  const lastSynced = value?.last_synced_at ? new Date(value.last_synced_at) : null;

  return (
    <div className="border border-slate-200 rounded-2xl p-4 bg-white" data-testid={`social-row-${platform}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl grid place-items-center text-white ${meta.badge}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <div className="font-bold text-slate-900 flex items-center gap-2">
              {meta.label}
              {verified ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200">
                  <CheckCircle2 className="w-3 h-3" /> Verified
                </span>
              ) : value?.handle ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  <AlertCircle className="w-3 h-3" /> Self-reported
                </span>
              ) : null}
            </div>
            {lastSynced && (
              <div className="text-[11px] text-slate-500 mt-0.5">
                Last updated {lastSynced.toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          title={oauthStatus === 'coming_soon' ? `Connect with ${meta.label} (coming soon)` : 'Connect'}
          disabled
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed inline-flex items-center gap-1"
          data-testid={`oauth-connect-${platform}`}
        >
          <LinkIcon className="w-3 h-3" />
          Connect (Coming Soon)
        </button>
      </div>

      <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-600">Handle / Username</Label>
          <Input
            value={form.handle}
            onChange={(e) => setForm({ ...form, handle: e.target.value })}
            placeholder={meta.placeholder}
            data-testid={`input-${platform}-handle`}
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Profile URL (optional)</Label>
          <Input
            value={form.profile_url}
            onChange={(e) => setForm({ ...form, profile_url: e.target.value })}
            placeholder="https://..."
            data-testid={`input-${platform}-url`}
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">{meta.metric_label}</Label>
          <Input
            type="number"
            min="0"
            value={form.follower_count}
            onChange={(e) => setForm({ ...form, follower_count: e.target.value })}
            placeholder="0"
            data-testid={`input-${platform}-followers`}
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">Engagement Rate (%)</Label>
          <Input
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={form.engagement_rate}
            onChange={(e) => setForm({ ...form, engagement_rate: e.target.value })}
            placeholder="0.0"
            data-testid={`input-${platform}-er`}
          />
        </div>
        <div>
          <Label className="text-xs text-slate-600">{meta.avg_label}</Label>
          <Input
            type="number"
            min="0"
            value={form.average_views}
            onChange={(e) => setForm({ ...form, average_views: e.target.value })}
            placeholder="0"
            data-testid={`input-${platform}-avg`}
          />
        </div>
        <div className="flex items-end gap-2">
          <Button type="submit" disabled={saving} className="w-full" data-testid={`save-${platform}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span className="ml-1.5">Save {meta.label}</span>
          </Button>
          {value?.handle && (
            <Button
              type="button"
              variant="outline"
              onClick={() => onRemove?.(platform)}
              data-testid={`remove-${platform}`}
            >
              Remove
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

const SocialAccountsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [oauthStatus, setOauthStatus] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/talent/social-accounts');
      setAccounts(r.data?.accounts || []);
      setOauthStatus(r.data?.oauth_status || {});
    } catch (e) {
      toast.error('Failed to load social accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const byPlatform = Object.fromEntries(accounts.map(a => [a.platform, a]));

  const onSave = (saved) => {
    setAccounts((prev) => {
      const idx = prev.findIndex(a => a.platform === saved.platform);
      if (idx === -1) return [...prev, saved];
      const next = [...prev];
      next[idx] = saved;
      return next;
    });
  };

  const onRemove = async (platform) => {
    if (!window.confirm(`Remove your ${platform} account?`)) return;
    try {
      await api.delete(`/talent/social-accounts/${platform}`);
      setAccounts((prev) => prev.filter(a => a.platform !== platform));
      toast.success(`${platform} removed`);
    } catch (e) {
      toast.error('Remove failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="social-accounts-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-lg font-extrabold text-slate-900">Social Accounts</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Connect Instagram, TikTok, and YouTube. Today metrics are <strong>self-reported</strong>;
            once we ship OAuth, your numbers will sync automatically.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reload-social">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          <span className="ml-1.5">Reload</span>
        </Button>
      </div>

      {loading ? (
        <div className="p-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-600" /></div>
      ) : (
        <div className="space-y-3">
          {['instagram', 'tiktok', 'youtube'].map(p => (
            <PlatformRow
              key={p}
              platform={p}
              value={byPlatform[p]}
              onSave={onSave}
              onRemove={onRemove}
              oauthStatus={oauthStatus[p]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SocialAccountsPanel;
